/**
 * ProjectChangeOrdersPanel — list of approved/paid/in-production/completed
 * change orders for a project. Mounts inside the Project Detail page.
 */
import { useEffect, useState } from "react";
import { apiClient } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Plus, ExternalLink, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { AddChangeOrderModal } from "./AddChangeOrderModal";
import { canEditAdditionalScope, isFinanceRole } from "./additionalScopeAccess";
import { decisionSummary } from "./itemDecisions";

const SHOW_STATUSES = new Set([
  "client_approved", "ready_to_send", "sent_to_client", "invoice_sent",
  "paid", "cleared_for_production", "in_production", "completed",
  // legacy
  "approved", "invoiced", "payment_pending",
]);

const STATUS_TONE = {
  paid:                   "bg-green-100 text-green-800",
  cleared_for_production: "bg-emerald-100 text-emerald-800",
  in_production:          "bg-purple-50 text-purple-700",
  completed:              "bg-zinc-100 text-zinc-700",
  invoice_sent:           "bg-sky-50 text-sky-700",
  sent_to_client:         "bg-indigo-50 text-indigo-700",
  ready_to_send:          "bg-blue-50 text-blue-700",
  client_approved:        "bg-emerald-50 text-emerald-700",
  approved:               "bg-emerald-50 text-emerald-700",
};

const formatCurrency = (n) => `$${Number(n || 0).toLocaleString()}`;

// Client approval/payment links go inactive 7 days after (re)send.
const linkDaysRemaining = (co) => {
  if (!co.link_expires_at || co.status !== "sent_to_client") return null;
  const ms = new Date(co.link_expires_at).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};

export default function ProjectChangeOrdersPanel({ projectId, projectName, canCreate = true }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  // Row open in the wizard for editing (null = creating a new one)
  const [editCo, setEditCo] = useState(null);
  // Needed only to decide who sees Edit — this panel lists rows that already
  // went to the client, so in practice that is finance.
  const [me, setMe] = useState(null);

  const load = async () => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await apiClient.get(`/change-orders?project_id=${projectId}`);
      setOrders(res.data.change_orders || []);
    } catch {
      setOrders([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => { apiClient.get("/auth/me").then(r => setMe(r.data)).catch(() => setMe(null)); }, []);

  const isFinance = isFinanceRole(me?.role);

  const visible = orders.filter(o => SHOW_STATUSES.has(o.status));
  const totalRevenue = visible.reduce((s, o) => s + (o.final_client_price || o.total_amount || 0), 0);

  return (
    <Card className="border border-border rounded-2xl shadow-none" data-testid="project-co-panel">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="label-text flex items-center gap-2">
            Additional Scope
            {visible.length > 0 && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium normal-case tracking-normal text-foreground tabular-nums">
                {visible.length} · {formatCurrency(totalRevenue)}
              </span>
            )}
          </CardTitle>
          <p className="text-[13px] text-muted-foreground mt-1.5">Change orders, upsells, and additional scope tied to this project.</p>
        </div>
        {canCreate && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAddOpen(true)}
            className="h-8 shrink-0 rounded-full px-3 text-[13px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
            data-testid="project-add-co-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-xs text-muted-foreground/70 py-3">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-sm text-muted-foreground py-3">No approved additional scope yet.</div>
        ) : (
          <div className="space-y-2">
            {visible.map(co => (
              <div key={co.id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-transparent border border-border" data-testid={`project-co-row-${co.short_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono text-muted-foreground/70">{co.short_id}</span>
                    <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${co.request_type === "upsell" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                      {co.request_type === "upsell" ? "Upsell" : "Change Order"}
                    </Badge>
                    <span className="font-medium text-sm truncate">{co.title || co.category}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {(co.scope_type || "").replace("_", " ")} · {co.production_method || "—"} · {co.timeline_impact ? `Timeline: ${co.timeline_impact}` : ""}
                    {decisionSummary(co) && <> · {decisionSummary(co)}</>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums">{formatCurrency(co.final_client_price || co.total_amount)}</div>
                  <Badge className={`text-[10px] capitalize mt-0.5 ${STATUS_TONE[co.status] || "bg-slate-100 text-slate-700"}`}>
                    {(co.status || "").replace(/_/g, " ")}
                  </Badge>
                  {linkDaysRemaining(co) !== null && (
                    <div className={`text-[10px] mt-0.5 ${linkDaysRemaining(co) <= 2 ? "text-red-600" : "text-muted-foreground/70"}`}>
                      {linkDaysRemaining(co) === 0 ? "Link expires today" : `Link: ${linkDaysRemaining(co)}d left`}
                    </div>
                  )}
                </div>
                {canEditAdditionalScope(co, { isFinance, user: me }) && (
                  <button
                    type="button"
                    onClick={() => setEditCo(co)}
                    className="text-muted-foreground/70 hover:text-foreground"
                    title="Edit"
                    data-testid={`project-co-edit-${co.short_id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                <Link to={`/change-orders?focus=${co.id}`} className="text-muted-foreground/70 hover:text-foreground">
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AddChangeOrderModal
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={projectId}
        projectName={projectName}
        onSaved={() => { setAddOpen(false); load(); }}
      />

      {/* Edit — the same wizard, hydrated from the row. Keyed on the row id so
          switching between two rows remounts with fresh state. */}
      {editCo && (
        <AddChangeOrderModal
          key={editCo.id}
          open
          onOpenChange={(v) => { if (!v) setEditCo(null); }}
          editingCo={editCo}
          projectId={projectId}
          projectName={projectName}
          forcedType={editCo.request_type === "upsell" ? "upsell" : "change_order"}
          onSaved={() => { setEditCo(null); load(); }}
        />
      )}
    </Card>
  );
}
